import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { FileSpreadsheet, Upload, Download, Trash2, Clock, CheckCircle, AlertCircle, Loader2, X, Settings, ChevronDown, ChevronUp, Save, RotateCcw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import type { ProfileProcessingQueue } from "@shared/schema";

interface ProfileSettings {
  systemPrompt: string; // Deprecated
  factPrompt: string;
  secondPassPrompt?: string;
}

const defaultSettings: ProfileSettings = {
  systemPrompt: "You are transforming demographic and behavioral data into clear, concise facts about radio station audiences. Focus on insights that would be valuable for sales and marketing purposes.",
  secondPassPrompt: "Make this grammatically correct: {fact_text_from_pass_1}",
  factPrompt: `ROLE
Return EXACTLY ONE clean, grammatical sentence about public radio listeners using the inputs below.
This function is called repeatedly. On each call:
• If {npr_percentage} is provided (non-empty) → produce a percentage fact.
• Else if {index} is provided (non-empty) → produce an index fact.
• Else (both empty) → return an empty string.

INPUTS (per call)
{category}        — Main category (from sheet name).
{subcategory}     — Subcategory question/context from column A.
{item}            — Specific response/attribute text.
{npr_percentage}  — NPR audience percentage (present ONLY for percentage facts).
{us_percentage}   — U.S. average percentage (present ONLY for percentage facts).
{index}           — Index value (present ONLY for index facts; 100 = average).

ABSOLUTE OUTPUT CONTRACT
• Output ONE sentence only. Start with a capital letter and end with ONE period.
• No labels, bullets, JSON, questions, placeholders, or extra commentary.
• Never output variable names or instructional phrases. Use the values only.
• Keep the sentence natural and sales-friendly.

SUBJECT
• Always use "Public radio listeners" as the subject (never "NPR listeners").
• Plural subject → plural verb ("listeners are / have / do"). Never use "is" with listeners.

CONVERTING ITEM TO NATURAL LANGUAGE
• Transform the item into a natural characteristic or behavior.
• For education levels: use "have [degree level]" (e.g., "have a bachelor's degree or higher").
• For behaviors: use present tense verbs (e.g., "shop at", "purchase", "own", "use").
• For demographics: use "are [characteristic]" (e.g., "are employed full-time").
• Keep brand names, product names, and specific terms exactly as provided.

PERCENTAGE FACT (when {npr_percentage} is provided)
• Write as: "<percentage>% of public radio listeners <natural action/characteristic>."
• Use the exact {npr_percentage} value provided, formatted cleanly (no trailing decimals if .0).
• If a comparison is meaningful, you may briefly note it, but focus on the NPR percentage.
• Example: "72% of public radio listeners have a bachelor's degree or higher."

INDEX FACT (when {index} is provided)
• Compute lift = |{index} − 100|. No decimals unless {index} includes them; strip trailing ".0".
• If {index} > 100 → "Public radio listeners are <lift>% more likely to <natural action/characteristic> than the average adult."
• If {index} < 100 → "Public radio listeners are <lift>% less likely to <natural action/characteristic> than the average adult."
• If {index} = 100 → "Public radio listeners are about as likely to <natural action/characteristic> as the average adult."
• Always use the exact phrase "the average adult" for comparison.
• Example: "Public radio listeners are 45% more likely to purchase organic products than the average adult."

ONE-SENTENCE ENFORCEMENT
• Return ONE sentence only, with ONE period at the end.
• Do not emit extra punctuation that creates another sentence.`,
};

// Hardcoded settings that are not user-editable
const HARDCODED_SETTINGS = {
  temperature: 0.3,
  maxTokens: 2500,  // Increased to match Tapscan processor
  model: "gpt-5-mini",
  includeIndices: true,
  includePercentages: true,
};

export default function ProfileQueue() {
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [citationText, setCitationText] = useState<string>('');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<ProfileSettings>(defaultSettings);
  const [promotingItems, setPromotingItems] = useState<Set<string>>(new Set());
  const [deletingItem, setDeletingItem] = useState<ProfileProcessingQueue | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch queue items
  const { data: queueItems = [], isLoading, refetch } = useQuery<ProfileProcessingQueue[]>({
    queryKey: ['/api/profile-queue'],
    refetchInterval: 3000, // Poll every 3 seconds for updates
  });

  // Fetch current settings
  const { data: fetchedSettings, isLoading: isLoadingSettings } = useQuery<ProfileSettings>({
    queryKey: ['/api/profile-settings'],
  });

  // Update local state when settings are fetched
  useEffect(() => {
    if (fetchedSettings) {
      setSettings(fetchedSettings);
    }
  }, [fetchedSettings]);

  // Track if we're currently adding to queue to prevent double-clicks
  const [isAddingToQueue, setIsAddingToQueue] = useState(false);
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx')) {
        toast({
          title: "Invalid File",
          description: "Please upload an Excel file (.xlsx)",
          variant: "destructive",
        });
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      // Auto-suggest citation based on filename
      const year = new Date().getFullYear();
      const month = new Date().toLocaleString('default', { month: 'long' });
      setCitationText(`NPR Profile Data, ${month} ${year}`);
    }
  };
  
  // Add file to queue mutation - using apiRequest
  const addToQueue = useMutation({
    mutationFn: async () => {
      // Prevent double-clicking
      if (isAddingToQueue) {
        throw new Error("Already processing, please wait...");
      }
      
      if (!selectedFile) {
        throw new Error("Please provide a file");
      }

      setIsAddingToQueue(true);

      const base64File = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result?.toString().split(',')[1];
          if (base64) {
            resolve(base64);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      return apiRequest('/api/profile-queue/add', 'POST', {
        fileName: selectedFile.name,
        fileData: base64File,
        citationText: citationText || `NPR Profile Data, ${new Date().toLocaleDateString()}`,
      });
    },
    onSuccess: () => {
      toast({
        title: "Added to Queue",
        description: "Your file has been added to the processing queue.",
      });
      setSelectedFile(null);
      setCitationText('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refetch();
      setIsAddingToQueue(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to add file to queue. Please try again.",
        variant: "destructive",
      });
      setIsAddingToQueue(false);
    },
  });

  // Delete queue item mutation - using apiRequest
  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/profile-queue/${id}`, 'DELETE');
    },
    onSuccess: () => {
      toast({
        title: "Item Deleted",
        description: "The queue item has been removed.",
      });
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete item.",
        variant: "destructive",
      });
    },
  });
  
  // Cancel processing mutation - using apiRequest
  const cancelProcessing = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/profile-queue/${id}/cancel`, 'POST');
    },
    onSuccess: () => {
      toast({
        title: "Processing Cancelled",
        description: "The file processing has been cancelled.",
      });
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: "Cancel Failed",
        description: error.message || "Failed to cancel processing.",
        variant: "destructive",
      });
    },
  });

  // Retry item mutation (for error/cancelled items)
  const retryItem = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/profile-queue/${id}/retry`, 'POST');
    },
    onSuccess: () => {
      toast({
        title: "Retry Queued",
        description: "The item has been queued for reprocessing.",
      });
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: "Retry Failed",
        description: error.message || "Failed to retry item.",
        variant: "destructive",
      });
    },
  });

  // Save settings mutation
  const saveSettings = useMutation({
    mutationFn: async () => {
      // Merge user-editable settings with hardcoded values
      const fullSettings = {
        ...settings,
        ...HARDCODED_SETTINGS,
      };
      return apiRequest("/api/profile-settings", "PUT", fullSettings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/profile-settings'] });
      toast({
        title: "Settings Saved",
        description: "NPR profile processing settings have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    },
  });


  const handleResetDefaults = () => {
    setSettings(defaultSettings);
    toast({
      title: "Settings Reset",
      description: "Settings have been reset to defaults. Click Save to apply.",
    });
  };

  // Download completed file
  const downloadFile = (item: ProfileProcessingQueue) => {
    if (item.status === 'completed' && item.outputData) {
      const blob = new Blob([Uint8Array.from(atob(item.outputData), c => c.charCodeAt(0))], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.outputFileName || `${item.fileName}_processed.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Handle promoting queue item to knowledge base
  const handlePromote = async (id: string) => {
    // Add this item to the promoting set
    setPromotingItems(prev => new Set(prev).add(id));
    
    try {
      // NPR Profile data is global - it applies to all stations
      // Use a special "global" station ID to indicate this is shared data
      const GLOBAL_STATION_ID = 'global-npr-profile';
      
      // Check if there are any stations in the system (for validation)
      const stations = await apiRequest('/api/stations');
      
      if (!stations || stations.length === 0) {
        toast({
          title: "No Stations Available",
          description: "Please create at least one station before promoting NPR Profile facts.",
          variant: "destructive",
        });
        // Remove from promoting set
        setPromotingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        return;
      }
      
      // Call the promotion endpoint with global station ID
      // The backend will handle this to make the facts available to all stations
      const promoteResponse = await apiRequest(`/api/profile-queue/${id}/promote`, 'POST', { 
        stationId: GLOBAL_STATION_ID,
        isGlobalData: true // Flag to indicate this is global NPR data
      });
      
      toast({
        title: "NPR Profile Facts Promoted",
        description: "Facts have been successfully promoted to the global knowledge base, available to all stations.",
      });
      
      // Refresh the queue data
      refetch();
    } catch (error) {
      console.error('Error promoting item:', error);
      toast({
        title: "Promotion Failed",
        description: error instanceof Error ? error.message : "Failed to promote facts to knowledge base.",
        variant: "destructive",
      });
    } finally {
      // Always remove from promoting set when done
      setPromotingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const getElapsedTime = (item: ProfileProcessingQueue) => {
    // Terminal states: completed, cancelled, error
    const isTerminalState = item.status === 'completed' || item.status === 'cancelled' || item.status === 'error';
    
    // If processing time is explicitly set (usually for completed items), use it
    if (item.processingTime) {
      const minutes = Math.floor(item.processingTime / 60);
      const seconds = item.processingTime % 60;
      return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    }
    
    // For terminal states without processingTime, calculate fixed time from creation to completion
    if (isTerminalState && item.createdAt) {
      // Use completedAt if available, otherwise use current time as a fallback
      const created = new Date(item.createdAt);
      const completed = item.completedAt ? new Date(item.completedAt) : new Date();
      const diff = Math.floor((completed.getTime() - created.getTime()) / 1000);
      const minutes = Math.floor(diff / 60);
      const seconds = diff % 60;
      return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    }
    
    // For non-terminal states (pending, processing), show live elapsed time
    if (!isTerminalState && item.createdAt) {
      const created = new Date(item.createdAt);
      const now = new Date();
      const diff = Math.floor((now.getTime() - created.getTime()) / 1000);
      const minutes = Math.floor(diff / 60);
      const seconds = diff % 60;
      return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    }
    
    return '-';
  };

  const getStatusDisplay = (item: ProfileProcessingQueue) => {
    // If processing and has currentStep, use that for more descriptive status
    if (item.status === 'processing' && item.currentStep) {
      // Check for batch-related steps
      if (item.currentStep.includes('batch')) {
        return { text: 'Batch Processing', variant: 'default' as const };
      }
      if (item.currentStep.includes('Excel')) {
        return { text: 'Creating Excel', variant: 'default' as const };
      }
    }
    
    // Default status display
    switch (item.status) {
      case 'pending':
        return { text: 'Queued', variant: 'secondary' as const };
      case 'processing':
      case 'batch_processing':
        return { text: 'Processing', variant: 'default' as const };
      case 'completed':
        return { text: 'Completed', variant: 'outline' as const };
      case 'error':
        return { text: 'Error', variant: 'destructive' as const };
      case 'cancelled':
        return { text: 'Cancelled', variant: 'secondary' as const };
      default:
        return { text: item.status, variant: 'outline' as const };
    }
  };

  if (isLoadingSettings || isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              <CardTitle>NPR Profile Data Processor</CardTitle>
            </div>
            <CardDescription>
              Transform NPR audience profile data into actionable insights
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Section */}
        <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            <h3 className="font-semibold">Upload New File</h3>
          </div>
          <p className="text-sm text-red-600 font-medium">
            Before processing any dataset, ensure the OpenAI Batch API dashboard is open in another browser window so you can monitor progress and prevent duplicate batches. Duplicate processing can result in hundreds of dollars in unnecessary API charges. As a best practice, avoid refreshing the browser while a batch is running.
          </p>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="file-upload">NPR Profile Excel File</Label>
                <Input
                  id="file-upload"
                  data-testid="input-file-upload"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xlsx"
                  disabled={addToQueue.isPending}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="citation-input">Citation Text</Label>
                <Input
                  id="citation-input"
                  data-testid="input-citation"
                  type="text"
                  value={citationText}
                  onChange={(e) => setCitationText(e.target.value)}
                  placeholder="e.g., NPR Profile Data, November 2024"
                  disabled={addToQueue.isPending}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => addToQueue.mutate()}
                disabled={!selectedFile || addToQueue.isPending || isAddingToQueue}
                data-testid="button-add-to-queue"
              >
                {(addToQueue.isPending || isAddingToQueue) ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Add to Queue
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Processing Queue Table */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Processing Queue</h3>
            <div className="text-sm text-gray-500">
              {queueItems.length} total items
            </div>
          </div>

          {/* Full-width progress bar - only show when actively processing */}
          {queueItems.some(item => item.status === 'processing' && item.progress && item.progress > 0 && item.progress < 100) && (
            <div className="w-full">
              {queueItems
                .filter(item => item.status === 'processing' && item.progress && item.progress > 0 && item.progress < 100)
                .map((item) => {
                  const currentPass = (item as any).currentPass || 1;
                  const isSecondPass = currentPass === 2;
                  
                  return (
                    <div key={item.id} className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          Processing: {item.fileName} {isSecondPass ? '(Pass 2: Grammar Correction)' : '(Pass 1: Fact Generation)'}
                        </span>
                        <span className="text-gray-500">{Math.round(item.progress || 0)}%</span>
                      </div>
                      <Progress 
                        value={item.progress || 0} 
                        className="w-full h-2" 
                      />
                      {item.currentStep && (
                        <p className="text-xs text-gray-500">{item.currentStep}</p>
                      )}
                      {item.message && (
                        <p className="text-xs text-gray-600">{item.message}</p>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {queueItems.length === 0 ? (
            <div className="border rounded-lg p-8 text-center text-gray-500">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No files in the queue</p>
              <p className="text-sm mt-1">Upload an NPR Profile file to get started</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Facts Found</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queueItems.map((item) => {
                    const statusInfo = getStatusDisplay(item);
                    const statusIcon = statusInfo.text === 'Processing' || statusInfo.text === 'Batch Processing' ? 
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" /> :
                      statusInfo.text === 'Completed' ? 
                      <CheckCircle className="h-3 w-3 mr-1" /> :
                      statusInfo.text === 'Error' ? 
                      <AlertCircle className="h-3 w-3 mr-1" /> :
                      statusInfo.text === 'Queued' ? 
                      <Clock className="h-3 w-3 mr-1" /> :
                      statusInfo.text === 'Cancelled' ? 
                      <X className="h-3 w-3 mr-1" /> : null;
                    
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {item.fileName}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge 
                              variant={statusInfo.variant}
                              className={statusInfo.text === 'Completed' ? 'text-green-600' : ''}
                            >
                              {statusIcon}
                              {statusInfo.text}
                            </Badge>
                            {item.status === 'error' && item.errorMessage && (
                              <span className="text-xs text-red-500 max-w-[200px] truncate" title={item.errorMessage}>
                                {item.errorMessage}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.factsFound > 0 ? item.factsFound.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {getElapsedTime(item)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {(item.status === 'processing' || item.status === 'batch_processing') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => cancelProcessing.mutate(item.id)}
                                data-testid={`button-cancel-${item.id}`}
                              >
                                <X className="h-3 w-3" />
                                Cancel
                              </Button>
                            )}
                            {(item.status === 'completed' || (item.status === 'error' && item.outputData)) && item.outputData && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => downloadFile(item)}
                                  data-testid={`button-download-${item.id}`}
                                >
                                  <Download className="h-3 w-3" />
                                  Download
                                </Button>
                                {item.status === 'completed' && !item.promoted && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handlePromote(item.id)}
                                    data-testid={`button-promote-${item.id}`}
                                    className="text-green-600 hover:text-green-700"
                                    disabled={promotingItems.has(item.id)}
                                  >
                                    {promotingItems.has(item.id) ? (
                                      <>
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Promoting...
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="h-3 w-3" />
                                        Promote
                                      </>
                                    )}
                                  </Button>
                                )}
                                {item.promoted && (
                                  <Badge variant="success" className="text-xs">
                                    Promoted
                                  </Badge>
                                )}
                              </>
                            )}
                            {(item.status === 'error' || item.status === 'cancelled') && !item.promoted && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => retryItem.mutate(item.id)}
                                data-testid={`button-retry-${item.id}`}
                                className="text-orange-600 hover:text-orange-700"
                                disabled={retryItem.isPending}
                              >
                                {retryItem.isPending ? (
                                  <>
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Retrying...
                                  </>
                                ) : (
                                  <>
                                    <RotateCcw className="h-3 w-3" />
                                    Retry
                                  </>
                                )}
                              </Button>
                            )}
                            {(item.status === 'completed' || item.status === 'error' || item.status === 'cancelled') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDeletingItem(item)}
                                data-testid={`button-delete-${item.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Settings Section (Collapsible) */}
        <Collapsible open={showSettings} onOpenChange={setShowSettings}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Processor Settings
              </div>
              {showSettings ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4 space-y-4 border rounded-lg p-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Configure the AI prompt that transforms NPR profile data into audience insights.
                Variables: {'{category}'}, {'{subcategory}'}, {'{item}'}, {'{npr_percentage}'}, {'{us_percentage}'}, {'{index}'}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="fact-prompt">Fact Generation Prompt (Pass 1)</Label>
              <Textarea
                id="fact-prompt"
                data-testid="input-fact-prompt"
                value={settings.factPrompt}
                onChange={(e) => setSettings({ ...settings, factPrompt: e.target.value })}
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                This prompt handles both percentage and index facts using conditional logic. Variables are replaced with actual data values.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="second-pass-prompt">Grammar Correction Prompt (Pass 2)</Label>
              <Textarea
                id="second-pass-prompt"
                data-testid="input-second-pass-prompt"
                value={settings.secondPassPrompt || "Make this grammatically correct: {fact_text_from_pass_1}"}
                onChange={(e) => setSettings({ ...settings, secondPassPrompt: e.target.value })}
                className="min-h-[100px] font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Second pass prompt for grammatical cleanup. Use {'{fact_text_from_pass_1}'} to reference the fact from the first pass.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => saveSettings.mutate()}
                disabled={saveSettings.isPending}
                data-testid="button-save-settings"
              >
                {saveSettings.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleResetDefaults}
                data-testid="button-reset-settings"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset to Default
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingItem} onOpenChange={() => setDeletingItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deletingItem?.promoted ? "Delete Promoted Item?" : "Delete Queue Item?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingItem?.promoted ? (
                <>
                  <strong className="text-destructive">Warning:</strong> This item has been promoted to the knowledge base. 
                  Deleting it will remove <strong>{deletingItem.factsFound || 0} facts</strong> from the knowledge base 
                  and affect any VBRs that use this data.
                  <br /><br />
                  This action cannot be undone.
                </>
              ) : (
                "This will permanently delete the queue item and its processing data. This action cannot be undone."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingItem) {
                  deleteItem.mutate(deletingItem.id);
                  setDeletingItem(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingItem?.promoted ? "Delete Anyway" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}